export const LEGAL = {
  privacy: {
    title: "Privacy Policy",
    body: `UnloadIt helps a family share the work of running a home. This page explains, in simple words, what information we collect, why, and what control you have. We follow India's Digital Personal Data Protection Act, 2023 and its Rules.

## 1. Who we are
UnloadIt is a free app run by Vishakha Gaikwad, Pune, Maharashtra, India ("we", "us"). For anything about your data, write to support.unloadit@gmail.com.

## 2. What we collect
• Account details: your name, email address, and your Google profile name and photo if you sign in with Google.
• Your home: the name of your home, the names of family members you add, their relation (optional), preferred language, and simple notes you write (for example "free on evenings").
• Home setup answers: whether you work outside home, work from home or manage the home full time; when family members are usually free and what they are good at; your cooking gas type and last booking date; milk, water and ration arrangements; food preferences (such as vegetarian, spice level, dislikes); the dates when regular medicine refills are due (we never ask for medicine names or illnesses); and your own estimate of how much of the home's planning sits on you. Only the home owner can see these answers, except each member's own name and availability.
• WhatsApp-only members (elders who don't use the app): only their first name, preferred language and the tasks given to them. They have no account. Each task message has a private link that works for that one task only.
• Tasks and lists: the tasks, grocery lists, meal plans, milk and dhobi records, kirana notes and helper attendance you choose to save.
• Helpers: the first name, type of work, agreed monthly amount and leave days of a helper (such as a maid or cook), only if you add them.
• Daily check-in: the mood you pick (Calm, Okay or Overloaded). Only you can see your check-ins. Other family members cannot.
• Basic technical data: the date and time you use the app and error logs, to keep the app working.
What we do NOT collect: phone contacts, location, bank or card details, government ID numbers, or payment information. UnloadIt never makes payments.

## 3. Photos and voice notes
When you use Scan or Speak, your photo or voice note is sent to an AI service only to read it. We do NOT keep the photo or the voice recording after it is read. Only the text and tasks you choose to confirm are saved. Nothing is saved until you press Confirm.

## 4. Why we use your data
• To create your account and your family home, and to suggest a home plan of repeating reminders from your setup answers.
• To turn your voice notes, photos and messages into tasks and lists.
• To suggest who could do a task, and write messages for family members.
• To show your weekly Unload Story and your share of the household load.
• To send you in-app notifications.
• To keep the app safe and fix problems.
We do not sell your data. We do not show ads. We do not use your data to train AI models.

## 5. Your consent
We use your data only after you agree, by ticking the box when you sign up. You can take back your consent at any time by deleting your account (Settings → Delete account) or by writing to support.unloadit@gmail.com. Taking back consent does not affect what was done before.

## 6. Who else handles your data
• Hosting: the app and its database are hosted by our app platform provider (Emergent) and its cloud partners.
• AI services: photos, voice notes and text you submit are processed by AI model providers (such as OpenAI, Google or Anthropic) through our app platform, only to give you a result.
• Google: if you choose "Continue with Google", Google confirms your sign-in.
These providers may process data on servers outside India. They handle it only to provide their service to us.

## 7. Sharing inside your family
Members of your home can see the tasks assigned to them, the shared task list, grocery lists and meal plans. They cannot see your check-ins. Helper salary notes are visible only to the person who added the helper. The home owner approves every person who asks to join the home. When you add family members or helpers, please let them know. We store only what you enter about them.

## 8. How long we keep data
We keep your data while your account is active. If you delete your account, we delete your personal data within 30 days, unless the law requires us to keep something for longer. Photos and voice recordings are never kept.

## 9. Keeping data safe
We use password protection, encrypted connections (HTTPS) and access limits. No system is perfectly secure. If a data breach affects you, we will inform you and the Data Protection Board of India as the law requires.

## 10. Your rights
You can:
• see the data we hold about you,
• correct or update it (most of it directly in the app),
• delete your account and data (Settings → Delete account),
• nominate someone to use these rights for you if you are unable to,
• raise a complaint (see Contact & Grievance).

## 11. Children
UnloadIt accounts are only for people aged 18 and above. A parent may add a child's first name to the home so tasks can be planned around them. Children do not get logins, and we do not collect any other information about them. Read "Children and UnloadIt" for details.

## 12. Changes to this policy
If we change this policy, we will show a notice in the app and update the date at the top.

## 13. Contact
Vishakha Gaikwad
support.unloadit@gmail.com
Pune, Maharashtra, India`,
  },
  terms: {
    title: "Terms of Use",
    body: `By creating an account, you agree to these terms. Please read them. They are short.

## 1. What UnloadIt is
UnloadIt is a free app that helps a family plan, share and remember household tasks. It uses AI to read voice notes, photos and messages, and to suggest tasks, meals and who could do what.

## 2. Who can use it
You must be 18 or older to create an account. You may add children to your home by first name only.

## 3. AI suggests, you decide
• AI can make mistakes. Always check what the AI suggests before you confirm it.
• Bill checks (milk, dhobi, kirana) and helper salary notes are suggestions to help you check. They are not final amounts. You decide what to pay.
• UnloadIt never makes payments and never takes money from anyone.
• Medicine tasks are reminders to refill or buy only. UnloadIt does not give medical advice, doses or diagnoses. Always follow your doctor.
• The daily check-in is not a medical or counselling service.

## 4. Shopping and recipe links
Buttons for Blinkit, Zepto, Swiggy Instamart and YouTube are simple search links that open those apps or websites. We are not connected to them, do not earn from them and are not responsible for their products, prices or delivery.

## 5. WhatsApp messages
When you tap "Send on WhatsApp", WhatsApp opens with a ready message. You choose whether to send it. We do not access your WhatsApp.

## 6. Your responsibilities
• Add family members and helpers only with their knowledge.
• Do not upload anything illegal, hurtful, or anyone's bank, card or ID details.
• Keep your password safe.

## 7. Free service, no guarantee
UnloadIt is free and offered "as is". We try our best to keep it working and accurate, but we cannot promise it will always be available or error-free. To the extent the law allows, we are not responsible for losses caused by relying on an AI suggestion without checking it.

## 8. Ending your account
You can delete your account at any time from Settings. We may suspend accounts that misuse the app.

## 9. Changes
We may update these terms. We will show a notice in the app when we do.

## 10. Law
These terms follow the laws of India. Courts in Pune, Maharashtra will handle any dispute.

Contact: support.unloadit@gmail.com`,
  },
  ai: {
    title: "How AI works in UnloadIt",
    body: `• What AI does: it listens to your voice note, reads your photo or message, and suggests tasks, amounts, meals and who could help.
• What AI does not do: it never pays, never decides final amounts, never gives medical advice, and never sends messages without you.
• Your photos and recordings: they are sent to an AI service only to be read, then they are not kept.
• You are in control: nothing is saved until you press Confirm. You can edit or delete any suggestion.
• AI can be wrong: please check numbers and names before confirming.
• Avoid sharing sensitive details: do not photograph bank cards, ID cards or medical reports.`,
  },
  children: {
    title: "Children and UnloadIt",
    body: `• Only adults (18+) can create an account or log in.
• A parent can add a child's first name (for example "Kabir, 8") so tasks like school project supplies can be planned.
• Children do not get logins, and we do not collect their photos, email, school, or any other details.
• Do not upload photos that show a child's face. School notes are fine.
• By adding a child, you confirm you are their parent or lawful guardian and you agree to us storing their first name and age.
• A parent can remove a child's name at any time from Family settings.`,
  },
  wellbeing: {
    title: "About the daily check-in",
    body: `The check-in is a simple way to notice how you're feeling and share the load at home. It is private: only you can see it.

UnloadIt is not a medical or counselling service, and it does not diagnose anything.

If you often feel overwhelmed, please talk to someone you trust. You can also call Tele-MANAS on 14416 or 1-800-891-4416. It is a free, 24×7 mental health helpline from the Government of India, in many Indian languages.

If you are in immediate danger, call 112.`,
  },
  money: {
    title: "Money notes",
    body: `"AI suggests. You decide. Please check before paying. UnloadIt never pays."

UnloadIt reads your milk card, dhobi slip, kirana note or helper attendance and does the arithmetic for you. It may misread handwriting. The amount shown is a suggestion to help you check, not a final bill. You decide what to pay, and you pay in your own way.

Bills that can go on autopay (electricity, mobile, Wi-Fi, DTH, gas pipeline, insurance, EMIs, credit cards, OTT, FASTag, society maintenance, online school fees) are not tracked in UnloadIt. Set them up on autopay in your bank or UPI app, and that's one less thing to remember.`,
  },
  contact: {
    title: "Contact and grievance",
    body: `Questions, corrections, or complaints about your data:

Grievance contact: Vishakha Gaikwad
Email: support.unloadit@gmail.com
Address: Pune, Maharashtra, India

We will reply within 7 days and try to solve your concern within 30 days.

If you are not satisfied with our reply, you can complain to the Data Protection Board of India.`,
  },
};

export const CONSENT = {
  signup: "I am 18 or older. I agree to the Privacy Policy and Terms of Use, and I allow UnloadIt to use my information to run my family home in the app.",
  note: "We use AI to read your voice notes and photos. Photos and recordings are not kept. Nothing is saved until you confirm. You can delete your account at any time.",
  join: "I am 18 or older. I agree to the Privacy Policy and Terms of Use to join this home.",
};
